import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api, fmtAUD, formatErr } from "../lib/api";
import { useNavigate } from "react-router-dom";
import RampPreview from "../components/RampPreview";
import {
  Save, RotateCcw, Eye, EyeOff, AlertTriangle, CheckCircle2, Calculator as CalcIcon,
  User, FileText, Ruler, Wrench, Box, Palette, Loader2,
} from "lucide-react";

const DEFAULT_INPUTS = {
  width: "", depth: "",
  left_rise: "", right_rise: "",
  left_wing: "", right_wing: "",
  left_kerb: false, right_kerb: false,
  wvs_left: false, wvs_right: false, wvs_top: false, wvs_bottom: false,
  notches: "", curved: false,
  colour: "Natural Black"
};

const SCOPES = [
  { key: "wholesale",         label: "Wholesale",         hint: "Supply only" },
  { key: "retail",            label: "Retail",            hint: "Supply only" },
  { key: "reseller",          label: "Reseller",          hint: "Supply only" },
  { key: "supply_install",    label: "Supply & Install",  hint: "Includes installation" },
  { key: "best_price_for_you", label: "Best Price For You", hint: "Baseline − discount %" },
];

export default function CalculatorPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin" || user?.role === "admin";
  const nav = useNavigate();

  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  const [pricing, setPricing] = useState(null);
  const [calc, setCalc] = useState(null);
  const [scope, setScope] = useState("wholesale");
  // "Best Price For You" controls
  const [baselineScope, setBaselineScope] = useState("supply_install");
  const [discountPct, setDiscountPct] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showDebug, setShowDebug] = useState(false);

  const [customer, setCustomer] = useState({ name: "", email: "", phone: "", address: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");

  useEffect(() => { api.get("/pricing").then((r) => setPricing(r.data)); }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      setBusy(true); setErr("");
      try {
        const numericInputs = {};
        for (const [k, v] of Object.entries(inputs)) {
          numericInputs[k] = (v === "" || v === null || v === undefined) && typeof v !== "boolean" ? 0 : v;
        }
        const { data } = await api.post("/calculate", {
          ...numericInputs,
          baseline_scope: baselineScope,
          discount_pct: Number(discountPct) || 0,
        });
        setCalc(data);
      } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
      finally { setBusy(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [inputs, baselineScope, discountPct]);

  const set = (k, v) => setInputs((s) => ({ ...s, [k]: v }));
  const reset = () => setInputs(DEFAULT_INPUTS);

  const colours = pricing?.colours || [];
  const selectedScope = useMemo(() => calc?.scopes?.[scope], [calc, scope]);

  // Validation
  const warnings = [];
  if (inputs.width > 2500) warnings.push("Width over 2500mm — use calculator multiple times.");
  if (inputs.left_rise > 0 && inputs.depth / inputs.left_rise < 8) warnings.push("Gradient steeper than 1:8 (LHS).");
  if (inputs.right_rise > 0 && inputs.depth / inputs.right_rise < 8) warnings.push("Gradient steeper than 1:8 (RHS).");
  if (calc?.weight?.total_kg > 80) warnings.push(`Weight ${calc.weight.total_kg}kg requires 2-man lift.`);

  // ====== MULTI-PRODUCT QUOTE FLOW ======
  // Pending cart of additional line items being added to the same quote
  // Loaded from sessionStorage on mount + persisted on every add
  const STORE_KEY = "eas_pending_quote";

  const [pendingCart, setPendingCart] = useState(() => {
    try {
      const raw = sessionStorage.getItem(STORE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  // If we have a pending cart, seed customer + carry quote number forward
  useEffect(() => {
    if (pendingCart?.customer) {
      setCustomer(pendingCart.customer);
    }
  }, [pendingCart]);

  const addAnotherItem = () => {
    if (!customer.name.trim()) { setErr("Customer name required."); return; }
    const newItem = {
      product_type: "wing_ramp",
      inputs,
      selected_scope: scope,
      label: `Wing Ramp — ${inputs.width}×${inputs.depth}mm`,
      preview_price: calc?.scopes?.[scope]?.sale_price || 0,
    };
    const updated = {
      customer,
      items: [...(pendingCart?.items || []), newItem],
      created_at: new Date().toISOString(),
    };
    sessionStorage.setItem(STORE_KEY, JSON.stringify(updated));
    setPendingCart(updated);
    // Navigate back to product selector to add another
    nav("/quote-portal/products?continue=1");
  };

  const finishAndSave = async () => {
    if (!customer.name.trim()) { setErr("Customer name required to save."); return; }
    setSaving(true); setErr("");
    try {
      // Build items list: existing cart + current input
      const currentItem = {
        product_type: "wing_ramp",
        inputs,
        selected_scope: scope,
        label: `Wing Ramp — ${inputs.width}×${inputs.depth}mm`,
      };
      const allItems = [...(pendingCart?.items || []), currentItem];
      // If only 1 item → use single-product format for backwards compat
      const payload = {
        customer_name: customer.name,
        customer_email: customer.email,
        customer_phone: customer.phone,
        customer_address: customer.address,
        notes: customer.notes,
      };
      if (allItems.length === 1) {
        payload.inputs = inputs;
        payload.selected_scope = scope;
        payload.product_type = "wing_ramp";
      } else {
        payload.items = allItems;
      }
      const { data } = await api.post("/quotes", payload);
      sessionStorage.removeItem(STORE_KEY);
      setPendingCart(null);
      setSavedMsg(`Saved as ${data.quote_number}`);
      setTimeout(() => nav(`/quote-portal/quotes/${data.id}`), 800);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const saveQuote = finishAndSave;

  return (
    <div className="space-y-6" data-testid="calculator-page">
      {/* Page header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#fda500] font-bold mb-1">New Quote</div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#0A1128]">Rubber Ramp Calculator</h1>
        </div>
        <div className="flex gap-2 items-center">
          <span className={`text-xs px-2.5 py-1 rounded-full ${busy ? "bg-orange-50 text-orange-700" : "bg-green-50 text-green-700"} flex items-center gap-1.5`}>
            {busy ? <><Loader2 size={12} className="animate-spin" /> Calculating</> : <><CheckCircle2 size={12} /> Live</>}
          </span>
          <button onClick={reset} className="px-3.5 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1.5 transition" data-testid="reset-btn">
            <RotateCcw size={13} /> Reset
          </button>
          {isAdmin && (
            <button onClick={() => setShowDebug((s) => !s)} className="px-3.5 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1.5 transition" data-testid="toggle-debug-btn">
              {showDebug ? <EyeOff size={13} /> : <Eye size={13} />} Debug
            </button>
          )}
        </div>
      </div>

      {/* 3-panel layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* LEFT PANEL — Inputs */}
        <div className="lg:col-span-4 space-y-5">
          <Card icon={User} title="Customer Details">
            <div className="space-y-3">
              <Field label="Customer name *" tid="cust-name" v={customer.name} onCh={(x)=>setCustomer(s=>({...s, name: x}))} />
              <Field label="Email" tid="cust-email" v={customer.email} onCh={(x)=>setCustomer(s=>({...s, email: x}))} />
              <Field label="Phone" tid="cust-phone" v={customer.phone} onCh={(x)=>setCustomer(s=>({...s, phone: x}))} />
              <Field label="Site address" tid="cust-address" v={customer.address} onCh={(x)=>setCustomer(s=>({...s, address: x}))} />
            </div>
          </Card>

          <Card icon={Wrench} title="Scope of Work">
            <div className="grid grid-cols-2 gap-2">
              {SCOPES.map((s) => {
                const active = scope === s.key;
                return (
                  <button
                    key={s.key}
                    onClick={() => setScope(s.key)}
                    data-testid={`scope-${s.key}`}
                    className={`text-left p-3 rounded-md border transition-all ${
                      active
                        ? "bg-[#254391] text-white border-[#254391] shadow-md"
                        : "bg-white text-gray-700 border-gray-200 hover:border-[#254391] hover:bg-blue-50/40"
                    } ${s.key === "best_price_for_you" ? "col-span-2" : ""}`}
                  >
                    <div className="text-sm font-semibold">{s.label}</div>
                    <div className={`text-[10px] mt-0.5 ${active ? "text-white/80" : "text-gray-500"}`}>{s.hint}</div>
                  </button>
                );
              })}
            </div>
            {scope === "best_price_for_you" && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md space-y-2.5" data-testid="best-price-panel">
                <div className="text-[10px] uppercase tracking-wider font-bold text-amber-800">Internal — Discount Configuration</div>
                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider font-semibold text-gray-600 mb-1">Baseline scope</label>
                    <select
                      data-testid="baseline-scope-select"
                      className="w-full px-2 py-1.5 border border-amber-300 rounded text-xs bg-white"
                      value={baselineScope}
                      onChange={(e) => setBaselineScope(e.target.value)}
                    >
                      <option value="wholesale">Wholesale</option>
                      <option value="retail">Retail</option>
                      <option value="reseller">Reseller</option>
                      <option value="supply_install">Supply &amp; Install</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider font-semibold text-gray-600 mb-1">Discount %</label>
                    <input
                      data-testid="discount-pct-input"
                      type="number"
                      min={0}
                      max={99}
                      step={0.5}
                      value={discountPct}
                      onChange={(e) => setDiscountPct(e.target.value)}
                      placeholder="e.g. 10"
                      className="w-full px-2 py-1.5 border border-amber-300 rounded text-xs font-mono bg-white"
                    />
                  </div>
                </div>
                <div className="text-[10px] text-gray-600 leading-relaxed">
                  Final customer price = baseline × (1 − discount). Internal cost &amp; margin stay hidden from customer PDF/email.
                </div>
              </div>
            )}
            <p className="text-[11px] text-gray-500 mt-2 leading-relaxed">
              Only the selected scope&apos;s pricing function is applied. Supply &amp; Install includes installation labour;
              the other three are supply-only.
            </p>
          </Card>

          <Card icon={Ruler} title="Dimensions (mm)">
            <div className="grid grid-cols-2 gap-3">
              <NumIn label="Width"      v={inputs.width}      onCh={(x)=>set("width", x)}      tid="in-width" />
              <NumIn label="Depth"      v={inputs.depth}      onCh={(x)=>set("depth", x)}      tid="in-depth" />
              <NumIn label="Left Rise"  v={inputs.left_rise}  onCh={(x)=>set("left_rise", x)}  tid="in-leftrise" />
              <NumIn label="Right Rise" v={inputs.right_rise} onCh={(x)=>set("right_rise", x)} tid="in-rightrise" />
              <NumIn label="Left Wing"  v={inputs.left_wing}  onCh={(x)=>set("left_wing", x)}  tid="in-leftwing" />
              <NumIn label="Right Wing" v={inputs.right_wing} onCh={(x)=>set("right_wing", x)} tid="in-rightwing" />
            </div>
          </Card>

          <Card icon={Box} title="Ramp Options">
            <div className="grid grid-cols-2 gap-3">
              <YN label="Left Kerb"   v={inputs.left_kerb}   onCh={(x)=>set("left_kerb", x)}   tid="in-leftkerb" />
              <YN label="Right Kerb"  v={inputs.right_kerb}  onCh={(x)=>set("right_kerb", x)}  tid="in-rightkerb" />
              <YN label="Curved Ramp" v={inputs.curved}      onCh={(x)=>set("curved", x)}      tid="in-curved" />
              <NumIn label="Notches"  v={inputs.notches}     onCh={(x)=>set("notches", x)}     tid="in-notches" min={0} />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <YN label="WVS Left"   v={inputs.wvs_left}   onCh={(x)=>set("wvs_left", x)}   tid="in-wvsleft" />
              <YN label="WVS Right"  v={inputs.wvs_right}  onCh={(x)=>set("wvs_right", x)}  tid="in-wvsright" />
              <YN label="WVS Top"    v={inputs.wvs_top}    onCh={(x)=>set("wvs_top", x)}    tid="in-wvstop" />
              <YN label="WVS Bottom" v={inputs.wvs_bottom} onCh={(x)=>set("wvs_bottom", x)} tid="in-wvsbottom" />
            </div>
            <div className="mt-3">
              <label className="block text-[11px] uppercase tracking-wider font-semibold text-gray-600 mb-1">
                <Palette size={11} className="inline mr-1 mb-0.5" /> Colour
              </label>
              <select
                data-testid="in-colour"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:border-[#254391] focus:ring-2 focus:ring-[#254391]/20 outline-none"
                value={inputs.colour}
                onChange={(e)=>set("colour", e.target.value)}
              >
                {colours.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.name}{c.surcharge ? "  (+surcharge)" : ""}
                  </option>
                ))}
              </select>
            </div>
          </Card>
        </div>

        {/* MIDDLE PANEL — Preview + Summary */}
        <div className="lg:col-span-4 space-y-5">
          <Card title="Live Ramp Preview">
            <div className="rounded-md overflow-hidden">
              <RampPreview {...inputs} />
            </div>
            {warnings.length > 0 && (
              <div className="mt-3 space-y-1.5" data-testid="warnings">
                {warnings.map((w) => (
                  <div key={w} className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 px-2.5 py-1.5 rounded-md">
                    <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                    <span>{w}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card title="Inputs Summary">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <Row k="Width × Depth" v={`${inputs.width} × ${inputs.depth} mm`} />
              <Row k="Rises (L / R)" v={`${inputs.left_rise} / ${inputs.right_rise} mm`} />
              <Row k="Wings (L / R)" v={`${inputs.left_wing} / ${inputs.right_wing} mm`} />
              <Row k="Kerbs (L / R)" v={`${inputs.left_kerb ? "Yes" : "No"} / ${inputs.right_kerb ? "Yes" : "No"}`} />
              <Row k="Notches" v={inputs.notches} />
              <Row k="Curved" v={inputs.curved ? "Yes" : "No"} />
              <Row k="WVS sides" v={[
                inputs.wvs_left && "L", inputs.wvs_right && "R", inputs.wvs_top && "T", inputs.wvs_bottom && "B"
              ].filter(Boolean).join("·") || "—"} />
              <Row k="Colour" v={inputs.colour} />
            </div>
          </Card>

          {isAdmin && showDebug && calc && (
            <DebugPanel calc={calc} inputs={inputs} scope={scope} />
          )}
        </div>

        {/* RIGHT PANEL — Prices */}
        <div className="lg:col-span-4 space-y-5">
          {/* Selected price big display */}
          <div className="bg-[#0A1128] text-white rounded-lg shadow-[0_8px_24px_rgba(10,17,40,0.16)] p-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#fda500]" />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#fda500] font-bold mb-2">
                {selectedScope?.label || "—"} Sale Price
              </div>
              <div className="font-mono text-4xl md:text-[42px] font-bold tracking-tight" data-testid="preview-price">
                {selectedScope ? fmtAUD(selectedScope.sale_price) : "—"}
              </div>
              <div className="text-[11px] uppercase tracking-wider text-white/60 mt-3 flex items-center gap-3">
                <span>Margin {selectedScope ? Math.round(selectedScope.margin_pct * 100) : 0}%</span>
                <span className="opacity-50">•</span>
                <span>Cost {selectedScope ? fmtAUD(selectedScope.scope_cost) : "—"}</span>
              </div>
            </div>
          </div>

          {/* All scope cards */}
          <Card title="All Scopes" icon={FileText}>
            <div className="space-y-2">
              {SCOPES.map((s) => {
                const sc = calc?.scopes?.[s.key];
                const active = scope === s.key;
                return (
                  <button
                    key={s.key}
                    onClick={() => setScope(s.key)}
                    className={`w-full flex items-center justify-between p-3 rounded-md border transition ${
                      active ? "border-[#fda500] bg-[#fff8e7]" : "border-gray-200 hover:border-gray-300 bg-white"
                    }`}
                  >
                    <div className="text-left">
                      <div className="text-sm font-semibold text-[#0A1128]">{s.label}</div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wider">{s.hint}</div>
                    </div>
                    <div className="font-mono text-base font-semibold text-[#254391]">
                      {sc ? fmtAUD(sc.sale_price) : "—"}
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Weight breakdown */}
          <Card title="Weight Breakdown">
            <div className="space-y-1.5 text-sm">
              <Row k="Ramp" v={`${calc?.weight?.ramp_kg ?? "—"} kg`} mono />
              <Row k="LHS Wing" v={`${calc?.weight?.lhs_wing_kg ?? "—"} kg`} mono />
              <Row k="RHS Wing" v={`${calc?.weight?.rhs_wing_kg ?? "—"} kg`} mono />
              <div className="border-t border-gray-200 my-2" />
              <Row k="TOTAL" v={`${calc?.weight?.total_kg ?? "—"} kg`} mono bold accent />
            </div>
          </Card>

          {/* Pending multi-product cart */}
          {pendingCart?.items?.length > 0 && (
            <div className="bg-blue-50/60 border border-[#254391]/30 rounded-lg p-4" data-testid="pending-cart">
              <div className="flex items-center justify-between mb-2.5">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#254391] font-bold">Items in this quote ({pendingCart.items.length})</div>
                <button
                  type="button"
                  onClick={() => { sessionStorage.removeItem(STORE_KEY); setPendingCart(null); }}
                  className="text-[10px] text-red-600 hover:underline"
                  data-testid="clear-cart-btn"
                >Clear</button>
              </div>
              <div className="space-y-1.5">
                {pendingCart.items.map((it, i) => (
                  <div key={it.id || `${it.product_type}-${i}`} className="flex items-center justify-between text-xs bg-white border border-slate-200 rounded px-2.5 py-1.5">
                    <div className="font-medium text-[#0A1128] truncate">{it.label}</div>
                    <div className="font-mono text-[#254391] font-semibold shrink-0 ml-2">{fmtAUD(it.preview_price || 0)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Save */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            {err && (
              <div className="mb-3 text-xs bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md flex items-center gap-2">
                <AlertTriangle size={13} /> {err}
              </div>
            )}
            {savedMsg && (
              <div className="mb-3 text-xs bg-green-50 border border-green-200 text-green-800 px-3 py-2 rounded-md flex items-center gap-2" data-testid="saved-msg">
                <CheckCircle2 size={13} /> {savedMsg}
              </div>
            )}
            <div className="space-y-2">
              <button
                onClick={finishAndSave}
                disabled={saving}
                data-testid="save-quote-btn"
                className="w-full bg-[#fda500] hover:bg-[#e59500] text-[#0A1128] font-bold tracking-wide py-3 rounded-md flex items-center justify-center gap-2 transition shadow-md disabled:opacity-60"
              >
                {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> {pendingCart?.items?.length ? "Finish & Send Quote" : "Save Quote"}</>}
              </button>
              <button
                onClick={addAnotherItem}
                disabled={saving}
                data-testid="add-another-btn"
                className="w-full bg-white border border-[#254391] text-[#254391] hover:bg-blue-50 font-semibold tracking-wide py-2.5 rounded-md flex items-center justify-center gap-2 transition text-sm disabled:opacity-60"
              >
                <Box size={14} /> Add Another Product to This Quote
              </button>
            </div>
            <p className="text-[10px] text-gray-500 mt-2 text-center">
              Quote will be saved with auto-generated number and the selected scope.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ icon: Icon, title, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 bg-gradient-to-r from-blue-50/40 to-transparent">
        {Icon && <Icon size={14} className="text-[#254391]" />}
        <div className="text-xs font-bold uppercase tracking-[0.14em] text-[#254391]">{title}</div>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Field({ label, v, onCh, tid }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider font-semibold text-gray-600 mb-1">{label}</label>
      <input
        data-testid={tid}
        value={v}
        onChange={(e) => onCh(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-[#254391] focus:ring-2 focus:ring-[#254391]/20 outline-none transition"
      />
    </div>
  );
}
function NumIn({ label, v, onCh, tid, min, placeholder }) {
  const display = v === "" || v === null || v === undefined ? "" : v;
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider font-semibold text-gray-600 mb-1">{label}</label>
      <input
        data-testid={tid}
        type="number"
        inputMode="decimal"
        min={min}
        step="any"
        value={display}
        placeholder={placeholder || `Enter ${label.toLowerCase()}`}
        onFocus={(e) => e.target.select()}
        onChange={(e) => {
          let raw = e.target.value;
          if (raw === "" || raw === "-") { onCh(""); return; }
          if (/^0\d/.test(raw)) raw = raw.replace(/^0+/, "");
          const num = Number(raw);
          onCh(Number.isFinite(num) ? num : "");
        }}
        onBlur={() => { /* leave empty as "" — do not force 0 */ }}
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:border-[#254391] focus:ring-2 focus:ring-[#254391]/20 outline-none transition"
      />
    </div>
  );
}
function YN({ label, v, onCh, tid }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider font-semibold text-gray-600 mb-1">{label}</label>
      <div className="grid grid-cols-2 gap-1">
        <button
          type="button"
          data-testid={`${tid}-no`}
          onClick={() => onCh(false)}
          className={`py-1.5 text-xs uppercase tracking-wider rounded-md border transition ${
            !v ? "bg-[#254391] text-white border-[#254391]" : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"
          }`}
        >No</button>
        <button
          type="button"
          data-testid={`${tid}-yes`}
          onClick={() => onCh(true)}
          className={`py-1.5 text-xs uppercase tracking-wider rounded-md border transition ${
            v ? "bg-[#fda500] text-[#0A1128] border-[#fda500] font-semibold" : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"
          }`}
        >Yes</button>
      </div>
    </div>
  );
}
function Row({ k, v, mono = false, bold = false, accent = false }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className={`text-gray-500 ${bold ? "font-semibold text-[#0A1128]" : ""}`}>{k}</span>
      <span className={`${mono ? "font-mono" : ""} ${bold ? "font-bold text-base" : ""} ${accent ? "text-[#254391]" : "text-gray-800"}`}>{v}</span>
    </div>
  );
}

function DebugPanel({ calc, inputs, scope }) {
  const sel = calc?.scopes?.[scope];
  return (
    <div className="bg-[#0A1128] text-white rounded-lg shadow-md overflow-hidden border border-[#1E3675]" data-testid="debug-panel">
      <div className="bg-gradient-to-r from-[#1E3675] to-[#0A1128] px-4 py-2.5 flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-[0.14em] text-[#fda500]">⌥ Admin · Formula Trace</div>
        <span className="text-[10px] uppercase text-white/50">{scope}</span>
      </div>
      <pre className="font-mono text-[11px] leading-[1.65] p-4 overflow-x-auto whitespace-pre-wrap">
{`weight:
  ramp     = (W × D × avg_rise / 2) × density
           = (${inputs.width} × ${inputs.depth} × ${(inputs.left_rise + inputs.right_rise) / 2} / 2) × 770.27 mm³
           = `}<span className="text-[#fda500]">{calc.weight.ramp_kg} kg</span>{`
  lhs_wing = ${calc.weight.lhs_wing_kg} kg
  rhs_wing = ${calc.weight.rhs_wing_kg} kg
  TOTAL    = `}<span className="text-[#fda500]">{calc.weight.total_kg} kg</span>{`

material+labour:
  variable = ${calc.weight.total_kg} × $${calc.material_labour_breakdown.rate_per_kg}/kg = ${fmtAUD(calc.material_labour_breakdown.variable)}
  fixed    = ${fmtAUD(calc.material_labour_breakdown.fixed)}
  M+L      = `}<span className="text-[#fda500]">{fmtAUD(calc.material_labour_cost)}</span>{`

additional_costs:
${(calc.additional_breakdown || []).map((b) => `  + ${b.label.padEnd(40)} ${fmtAUD(b.value)}`).join("\n") || "  (none)"}
  TOTAL_ADDITIONAL = `}<span className="text-[#fda500]">{fmtAUD(calc.additional_costs)}</span>{`

contingency (${(calc.contingency_pct * 100).toFixed(2)}%) on ${fmtAUD(calc.subtotal)}
                   = ${fmtAUD(calc.contingency)}
cost_after_cont    = `}<span className="text-[#fda500]">{fmtAUD(calc.cost_after_contingency)}</span>{`

scope: ${scope.toUpperCase()} (margin ${sel ? Math.round(sel.margin_pct * 100) : 0}%)
${(sel?.scope_extras || []).map((b) => `  + ${b.label.padEnd(40)} ${fmtAUD(b.value)}`).join("\n")}
  scope_cost  = ${fmtAUD(sel?.scope_cost)}
  FINAL_PRICE = `}<span className="text-[#fda500]">{fmtAUD(sel?.sale_price)}</span>
      </pre>
    </div>
  );
}
